<template>
  <nav class="navbar">
    <div id="navbarInner" class="navbar-inner">
      <ul class="navbar-menu">
        <StorageMenuOldNavigationNavbarFirstLevel
          v-for="(item, i) in links"
          :key="i"
          :is-open="i === activeFirstLevel"
          :is-mobile-open="isMobileOpen && i === activeFirstLevel"
          :active-first-level="activeFirstLevel"
          :is-mobile="isUntilDesktop"
          v-bind="item"
          @click="(event: MouseEvent) => onToggle(i, item, event)"
          @touchend="onClickMobile(i, item)"
        />
      </ul>

      <Transition name="overlay-fade">
        <div
          v-if="isMobileOpen && isUntilDesktop"
          class="navbar-first-overlay"
          @touchend="onCloseMobile"
        />
      </Transition>
    </div>
  </nav>
  <Teleport to="body">
    <Transition name="fade-absolute">
      <div
        v-if="activeFirstLevel !== -1 && !isUntilDesktop"
        class="fixed top-0 left-0 z-300 h-screen w-screen bg-black/60 backdrop-blur-2xl"
        @click="onClose"
      />
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import type { NavbarFirstLevelItem } from '../../helper'

const { isUntilDesktop } = useViewport()
withDefaults(
  defineProps<{
    links: NavbarFirstLevelItem[]
  }>(),
  {
    links: () => [],
  },
)

const emit = defineEmits(['closeMobileMenu'])

const activeFirstLevel = ref<number>(-1)
const route = useRoute()
const isMobileOpen = ref<boolean>(false)
const subtreeIndex = useSubtreeIndex()

function onToggle(
  index: number,
  item: NavbarFirstLevelItem,
  event?: MouseEvent,
) {
  if (
    isUntilDesktop.value ||
    (item.subtree.length === 0 && item.link.type !== 'search')
  ) {
    return
  }

  const target = event?.target as HTMLElement | null
  // Only toggle if clicking on the button itself (or its children like spans)
  if (target && !target.closest('.navbar-first-link')) {
    return
  }

  if (activeFirstLevel.value === index) {
    activeFirstLevel.value = -1
  } else {
    activeFirstLevel.value = index
  }
}

function onClose() {
  activeFirstLevel.value = -1
}

function onClickMobile(index: number, item: NavbarFirstLevelItem) {
  if (
    !isUntilDesktop.value ||
    (item.subtree.length === 0 && item.link.type !== 'search')
  ) {
    return
  }

  if (activeFirstLevel.value === -1) {
    activeFirstLevel.value = index
    isMobileOpen.value = true
  }
}

function onCloseMobile() {
  if (subtreeIndex.value !== -1) {
    subtreeIndex.value = -1
    return
  }

  if (activeFirstLevel.value !== -1) {
    activeFirstLevel.value = -1
    isMobileOpen.value = false
  }
}

watch(
  () => route.fullPath,
  () => {
    setTimeout(() => {
      onClose()

      if (isUntilDesktop.value) {
        emit('closeMobileMenu')
      }
    }, 250)
  },
)
</script>

<style>
@reference '~/assets/css/tailwind.css';
.navbar {
  @apply belowDesktop:fixed belowDesktop:top-0 belowDesktop:right-0 belowDesktop:bottom-0 belowDesktop:w-3/5 belowDesktop:overflow-hidden belowDesktop:bg-background;
  @apply desktop:!block;
  @apply belowTablet:fixed belowTablet:top-0 belowTablet:right-0 belowTablet:bottom-0 belowTablet:!w-full belowTablet:overflow-hidden belowTablet:bg-background;
  @variant belowTablet {
    z-index: 200;
  }

  @variant belowDesktop {
    z-index: 200;
  }
}
.navbar-menu {
  @apply tablet:border-t tablet:border-solid tablet:border-border;
  @apply desktop:m-0 desktop:flex desktop:border-0 desktop:pt-0;
  padding-top: 3rem;
  @variant tablet {
    padding-top: 1rem;
    margin-top: 5rem;
  }
}
</style>
